import { memo, useEffect, useMemo, useState } from "react";
import { FinalizeSharePanel } from "../components/FinalizeSharePanel";
import { Option } from "../components/Option";
import { OptionsCategory } from "../components/OptionsCategory";
import { OptionsMenu as Menu } from "../components/OptionsMenu";
import { OptionsSubCategory } from "../components/OptionsSubCategory";
import { useMenuStore } from "../store/MenuStore";
import {
  buildExterior360Config,
  buildInteriorCuratedConfig,
} from "../util/renderOptions";
import {
  MenuCategoryData,
  MenuOptionData,
  MenuSubCategoryData,
} from "../util/menuDataFromCsv";

export function IMenu() {
  const [openCategoryId, setOpenCategoryId] = useState<string | undefined>();

  // Read menu state and loader actions from the central CSV-backed store.
  const selectedModelName = useMenuStore((state) => state.selectedModelName);
  const activeMenuId = useMenuStore((state) => state.activeMenuId);
  const allMenuDataById = useMenuStore((state) => state.allMenuDataById);
  const menuData = useMenuStore((state) => state.menuData);
  const configSlots = useMenuStore((state) => state.configSlots);
  const categorySubTitles = useMenuStore((state) => state.categorySubTitles);
  const loadMenuData = useMenuStore((state) => state.loadMenuData);
  const exterior360Config = useMemo(
    () => buildExterior360Config(allMenuDataById.exterior, configSlots),
    [allMenuDataById.exterior, configSlots],
  );
  const interiorCuratedConfig = useMemo(
    () => buildInteriorCuratedConfig(allMenuDataById.interior, configSlots),
    [allMenuDataById.interior, configSlots],
  );
  const firstCategoryId = menuData.categories[0]?.id;
  const categoryIdsKey = useMemo(
    () => menuData.categories.map((category) => category.id).join("|"),
    [menuData.categories],
  );

  // Load the CSV menu data for the selected model once; tab switches reuse it.
  useEffect(() => {
    loadMenuData();
  }, [loadMenuData, selectedModelName]);

  useEffect(() => {
    setOpenCategoryId(firstCategoryId);
  }, [activeMenuId, categoryIdsKey, firstCategoryId, selectedModelName]);

  if (activeMenuId === "finalize") {
    return (
      <Menu title={selectedModelName} subTitle="Download and share renders">
        <FinalizeSharePanel
          exteriorConfig={exterior360Config}
          interiorConfig={interiorCuratedConfig}
          modelName={selectedModelName}
        />
      </Menu>
    );
  }

  return (
    <Menu
      title={menuData.title}
      subTitle={menuData.subTitle}
      activeChildId={openCategoryId}
    >
      {menuData.categories.map((category) => (
        <OptionsCategory
          key={category.id}
          id={category.id}
          title={category.title}
          subTitles={categorySubTitles[category.id] || []}
          isOpen={openCategoryId === category.id}
          onClick={() =>
            setOpenCategoryId((currentCategoryId) =>
              currentCategoryId === category.id ? undefined : category.id,
            )
          }
        >
          {category.subCategories.map((subCategory) => (
            <OptionsSubCategory key={subCategory.id} title={subCategory.title}>
              {subCategory.options.map((option) => (
                <MenuOption
                  key={option.id}
                  category={category}
                  subCategory={subCategory}
                  option={option}
                />
              ))}
            </OptionsSubCategory>
          ))}
        </OptionsCategory>
      ))}
    </Menu>
  );
}

type MenuOptionProps = {
  category: MenuCategoryData;
  subCategory: MenuSubCategoryData;
  option: MenuOptionData;
};

const MenuOption = memo(function MenuOption({
  category,
  subCategory,
  option,
}: MenuOptionProps) {
  // Selection is calculated from config slots, including Is Selected Override expressions.
  const selected = useMenuStore((state) => state.isOptionSelected(option));

  // Clicking an option applies its command, cascades, prerequisite updates, and save behavior.
  const selectOption = useMenuStore((state) => state.selectOption);

  return (
    <Option
      toolTip={option.toolTip}
      backgroundImage={option.backgroundImage}
      selected={selected}
      onClick={() => selectOption(category, subCategory, option)}
    />
  );
});
